import { VendorSetupDetails } from '@/contracts/vendor-setup';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { X } from 'lucide-react';

interface Props {
  data: VendorSetupDetails;
  onChange: (data: VendorSetupDetails) => void;
}

const CATEGORIES = [
  { value: 'crm', label: 'CRM' },
  { value: 'lead-generation', label: 'Lead Generation' },
  { value: 'coaching', label: 'Coaching' },
  { value: 'marketing-automation', label: 'Marketing Automation' },
  { value: 'websites', label: 'Websites' },
  { value: 'transaction-management', label: 'Transaction Management' },
  { value: 'productivity', label: 'Productivity' },
  { value: 'data-analytics', label: 'Data & Analytics' },
  { value: 'signage-branding', label: 'Signage & Branding' },
  { value: 'client-gifting-events', label: 'Client Gifting & Events' },
  { value: 'education-licensing', label: 'Education & Licensing' },
  { value: 'print-direct-mail', label: 'Print & Direct Mail' },
];

const BEST_FOR_OPTIONS = [
  'Solo agents',
  'Team leaders',
  'New agents',
  'Experienced agents',
  'Luxury specialists',
  'Investment focus',
  'First-time buyers',
  'Commercial agents',
  'Property managers',
];

export function SetupDetailsStep({ data, onChange }: Props) {
  const toggleCategory = (category: string) => {
    if (data.categories.includes(category)) {
      onChange({ ...data, categories: data.categories.filter(c => c !== category) });
    } else {
      onChange({ ...data, categories: [...data.categories, category] });
    }
  };

  const toggleBestFor = (option: string) => {
    if (data.best_for.includes(option)) {
      onChange({ ...data, best_for: data.best_for.filter(b => b !== option) });
    } else {
      onChange({ ...data, best_for: [...data.best_for, option] });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold mb-2">Product Details</h2>
        <p className="text-muted-foreground">
          Tell agents what your product does and who it's for.
        </p>
      </div>

      {/* Product Name */}
      <div className="space-y-2">
        <Label htmlFor="title">Product Name *</Label>
        <Input
          id="title"
          value={data.title}
          onChange={(e) => onChange({ ...data, title: e.target.value })}
          placeholder="e.g., LeadFlow Pro"
        />
      </div>

      {/* Subtitle */}
      <div className="space-y-2">
        <Label htmlFor="subtitle">Tagline</Label>
        <Input
          id="subtitle"
          value={data.subtitle || ''}
          onChange={(e) => onChange({ ...data, subtitle: e.target.value })}
          placeholder="e.g., The all-in-one CRM for top-producing agents"
        />
        <p className="text-sm text-muted-foreground">A short, catchy description (1 sentence)</p>
      </div>

      {/* Description */}
      <div className="space-y-2">
        <Label htmlFor="description">Full Description *</Label>
        <Textarea
          id="description"
          value={data.description}
          onChange={(e) => onChange({ ...data, description: e.target.value })}
          placeholder="Describe your product in detail. What problem does it solve? What makes it unique?"
          rows={5}
        />
        <p className="text-sm text-muted-foreground">{data.description.length} characters</p>
      </div>

      {/* Service Type */}
      <div className="space-y-2">
        <Label>Product Type *</Label>
        <Select
          value={data.service_type}
          onValueChange={(value: 'service' | 'education' | 'gear') => 
            onChange({ ...data, service_type: value })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="service">Software / Service</SelectItem>
            <SelectItem value="education">Course / Training</SelectItem>
            <SelectItem value="gear">Physical Product / Gear</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Categories */}
      <div className="space-y-3">
        <Label>Categories * (select all that apply)</Label>
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((cat) => (
            <Badge
              key={cat.value}
              variant={data.categories.includes(cat.value) ? 'default' : 'outline'}
              className="cursor-pointer"
              onClick={() => toggleCategory(cat.value)}
            >
              {data.categories.includes(cat.value) && <X className="h-3 w-3 mr-1" />}
              {cat.label}
            </Badge>
          ))}
        </div>
      </div>

      {/* Best For */}
      <div className="space-y-3">
        <Label>Best For (who should use this?)</Label>
        <div className="flex flex-wrap gap-2">
          {BEST_FOR_OPTIONS.map((option) => (
            <Badge
              key={option}
              variant={data.best_for.includes(option) ? 'secondary' : 'outline'}
              className="cursor-pointer"
              onClick={() => toggleBestFor(option)}
            >
              {data.best_for.includes(option) && <X className="h-3 w-3 mr-1" />}
              {option}
            </Badge>
          ))}
        </div>
      </div>
    </div>
  );
}
